import { useState } from 'react'
import type { JSX } from 'react'

import { Flex } from '@buffer-mono/popcorn'

import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { useCommentsEngagementData } from './useCommentsListData'
import { NoBackfilledCommentsCard } from '../NoBackfilledCommentsCard'
import { NoRealtimeHistoryCard } from '../NoRealtimeHistoryCard'
import type { EmptyStateChannel } from '../EmptyStates/types'
import { useUnrepliedCount } from '../../../hooks/comments/useUnrepliedCount'
import { COMMENT_STATUS } from '../../../types'
import type { CommentStatusWithAll } from '../../../types'
import { isGoogleBusinessProfile } from '../../../util/channelHelpers'
import {
  ActivelyEngaging,
  CommentScoreIncreased,
  DefaultEmptyState,
  ListeningForComments,
  NoCommentsFound,
  RepliedToManyComments,
  ResolvingExplainerCard,
} from '../EmptyStates'

type UseEmptyStateElementProps = {
  channelIds: string[]
  hasComments: boolean
  hasActiveFilters: boolean
  status: CommentStatusWithAll | undefined
  onClearFilters: () => void
  onNavigateToUnreplied?: () => void
  isLastVisibleComment: boolean
  allCommentsLocallyResolved: boolean
  hasLocallyUnresolvedComments?: boolean
  channels?: EmptyStateChannel[]
  postId?: string
  unrepliedCount?: number
  totalCommentCount?: number
  postTotalCommentCount?: number
  isPostOlderThanRetentionWindow?: boolean
  /** Selected channel when exactly one is in view; null for multi-channel. */
  singleChannelId?: string | null
  /** Whether that single channel is realtime-sync (webhook/firehose) — it never backfills. */
  isSingleChannelRealtime?: boolean
}

type PusherEventData = {
  channelId?: string
}

/**
 * Picks which empty-state element to render (if any) from the comment list's
 * state. Pure decision tree — `unrepliedCount` / `totalCommentCount` are passed
 * in. The public `useEmptyStateRenderer` wraps this with count-fetching and the
 * show-with-comments decision.
 */
export const useEmptyStateElement = ({
  channelIds,
  hasComments,
  hasActiveFilters,
  status,
  onClearFilters,
  onNavigateToUnreplied,
  isLastVisibleComment,
  allCommentsLocallyResolved,
  hasLocallyUnresolvedComments,
  channels = [],
  postId,
  unrepliedCount,
  totalCommentCount,
  postTotalCommentCount,
  isPostOlderThanRetentionWindow = false,
  singleChannelId = null,
  isSingleChannelRealtime = false,
}: UseEmptyStateElementProps): { EmptyState: JSX.Element | null } => {
  const engagementData = useCommentsEngagementData({
    channelIds,
  })

  // First-connect explainer rendered inside the "listening" empty state.
  // Realtime channels never backfill, so they get the realtime variant (shown
  // whenever the channel is empty); polling channels get the backfill variant,
  // which self-gates on the channel's first backfill finishing with no comments.
  const firstConnectNotice = isSingleChannelRealtime ? (
    <NoRealtimeHistoryCard />
  ) : (
    <NoBackfilledCommentsCard channelId={singleChannelId} />
  )
  const [processingLastComment, setProcessingLastComment] = useState(false)

  const { score, engagement } = engagementData
  const {
    hasIncreased,
    current: currentScore,
    delta: scoreDelta,
    isLoading: isScoreLoading,
  } = score
  const {
    isActivelyEngaging,
    activeTime,
    repliedToManyComments,
    commentsReplied,
  } = engagement

  const scoreIncreased =
    hasIncreased && currentScore != null && scoreDelta != null

  usePusherEvent(
    PusherEvent.COMMENT_REPLY_PROCESSING,
    ({ channelId: pusherChannelId }: PusherEventData) => {
      if (channelIds.includes(pusherChannelId ?? '') && isLastVisibleComment) {
        setProcessingLastComment(true)
      }
    },
  )

  usePusherEvent(
    PusherEvent.COMMENT_REPLIED,
    ({ channelId: pusherChannelId }: PusherEventData) => {
      if (channelIds.includes(pusherChannelId ?? '') && processingLastComment) {
        setProcessingLastComment(false)
      }
    },
  )

  // The 'all' filter returns replied comments too, so list-empty doesn't mean
  // "no unreplied". Use the count instead, treating locally-resolved as 0 and
  // letting an explicit un-resolve (e.g. undo dismiss) override a stale count.
  const isAllViewWithNoUnreplied =
    status === COMMENT_STATUS.All &&
    !hasLocallyUnresolvedComments &&
    (unrepliedCount === 0 || allCommentsLocallyResolved)

  // The Replied and Dismissed views *show* resolved comments, so a populated
  // list there must never render an empty state. Replying again to an
  // already-replied comment (or re-dismissing) locally re-resolves it, flipping
  // `allCommentsLocallyResolved` true — but the comment stays in view. Without
  // this guard that bypasses the suppression below and falls through to
  // `NoCommentsFound`, showing "you haven't replied to any comments yet" above
  // the still-present list. (Unreplied/All legitimately swap to a caught-up
  // state when resolved, because resolving removes items from those views.)
  if (
    hasComments &&
    (status === COMMENT_STATUS.Replied || status === COMMENT_STATUS.Dismissed)
  ) {
    return { EmptyState: null }
  }

  // Suppress the empty state while unresolved comments are still visible.
  // For 'all', only proceed once the unreplied count is confirmed at 0.
  if (hasComments && !allCommentsLocallyResolved) {
    if (status !== COMMENT_STATUS.All) {
      return { EmptyState: null }
    }
    if (!isAllViewWithNoUnreplied) {
      return { EmptyState: null }
    }
  }

  // Engagement / listening empty states for 'unreplied' and 'all'.
  // Skip when filters are active and the list is empty — the emptiness is the
  // filter, not the channel state, so fall through to NoCommentsFound below.
  if (
    (status === COMMENT_STATUS.Unreplied || isAllViewWithNoUnreplied) &&
    (!hasActiveFilters || hasComments)
  ) {
    // By-post: skip engagement states. Use postTotalCommentCount (not
    // hasComments, which only reflects the active filter) to tell
    // "never received" (0) from "all replied" (> 0).
    if (postId) {
      if (postTotalCommentCount === 0) {
        const isGBP = isGoogleBusinessProfile(channels)
        return {
          EmptyState: (
            <ListeningForComments
              isGBP={isGBP}
              isPostView
              isOlderThanRetentionWindow={isPostOlderThanRetentionWindow}
              notice={firstConnectNotice}
            />
          ),
        }
      }
      // "All caught up" is only meaningful for Unanswered. The 'all' filter
      // still shows replied/dismissed below, so don't overlay it.
      if (
        status === COMMENT_STATUS.Unreplied &&
        postTotalCommentCount !== undefined
      ) {
        return {
          EmptyState: (
            <DefaultEmptyState
              justResolvedLastComment={allCommentsLocallyResolved}
              channels={channels}
              postId={postId}
            />
          ),
        }
      }
      return { EmptyState: null }
    }

    // Wait for the score query to settle before picking a variant —
    // rendering now would play EmptyStateWrapper's expand animation twice
    // (placeholder, then the real variant once score loads).
    if (isScoreLoading) {
      return { EmptyState: null }
    }

    // Only animate when the user just resolved comments in-session, not on
    // page-load count-based detection.
    const shouldAnimate = allCommentsLocallyResolved

    if (scoreIncreased) {
      return {
        EmptyState: (
          <CommentScoreIncreased
            score={currentScore}
            scoreDelta={scoreDelta}
            shouldAnimate={shouldAnimate}
            channels={channels}
            postId={postId}
          />
        ),
      }
    }
    if (isActivelyEngaging) {
      return {
        EmptyState: (
          <ActivelyEngaging
            activeTime={activeTime}
            shouldAnimate={shouldAnimate}
            channels={channels}
            postId={postId}
          />
        ),
      }
    }
    if (repliedToManyComments) {
      return {
        EmptyState: (
          <RepliedToManyComments
            score={commentsReplied}
            shouldAnimate={shouldAnimate}
            channels={channels}
            postId={postId}
          />
        ),
      }
    }

    // Distinguish "never received" (totalCommentCount === 0) from "all
    // replied" (> 0, unreplied = 0). Fall back to a hasComments heuristic
    // while the count is still loading.
    const neverReceivedComments =
      totalCommentCount === 0 ||
      (totalCommentCount === undefined &&
        !hasComments &&
        !allCommentsLocallyResolved)
    if (neverReceivedComments) {
      const isGBP = isGoogleBusinessProfile(channels)
      return {
        EmptyState: (
          <ListeningForComments isGBP={isGBP} notice={firstConnectNotice} />
        ),
      }
    }

    return {
      EmptyState: (
        <DefaultEmptyState
          justResolvedLastComment={shouldAnimate}
          channels={channels}
          postId={postId}
        />
      ),
    }
  }

  // By-post 'all' with no history: show "listening". 'unreplied' is handled
  // above; replied/dismissed get status-specific copy via NoCommentsFound.
  // Posts with prior comments fall through to CommentsSummaryBar.
  if (
    postId &&
    !hasComments &&
    !hasActiveFilters &&
    status === COMMENT_STATUS.All
  ) {
    if (postTotalCommentCount === 0) {
      const isGBP = isGoogleBusinessProfile(channels)
      return {
        EmptyState: (
          <ListeningForComments
            isGBP={isGBP}
            isPostView
            isOlderThanRetentionWindow={isPostOlderThanRetentionWindow}
            notice={firstConnectNotice}
          />
        ),
      }
    }
    return { EmptyState: null }
  }

  const noCommentsFound = (
    <NoCommentsFound
      status={status}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      onNavigateToUnreplied={onNavigateToUnreplied}
      channels={channels}
    />
  )

  // Dismissed list view: pair with the "How it works" card. Wrap together
  // so they occupy one grid column.
  if (status === COMMENT_STATUS.Dismissed && !hasActiveFilters && !postId) {
    return {
      EmptyState: (
        <Flex direction="column" gap="xl" align="center">
          {noCommentsFound}
          <ResolvingExplainerCard />
        </Flex>
      ),
    }
  }

  return { EmptyState: noCommentsFound }
}

type UseCommentsEmptyStateProps = Omit<
  UseEmptyStateElementProps,
  'unrepliedCount' | 'totalCommentCount'
> & {
  /**
   * Whether the current list has comments in it — plain `comments.hasComments`,
   * NOT gated on `query.data`. Drives `showEmptyStateWithComments`, and is kept
   * deliberately distinct from the element picker's `hasComments`
   * (`!!query.data && comments.hasComments`).
   */
  hasCommentsInList: boolean
}

/**
 * Owns the comment list's empty-state concern: fetches the unreplied/total
 * counts, picks the empty-state element (via `useEmptyStateElement`), and
 * decides whether to show the celebratory empty state *above* a still-visible
 * list — either the user just resolved every card locally, or the 'all' filter
 * shows replied comments while the unreplied count is confirmed at 0.
 */
export const useCommentsEmptyState = ({
  hasCommentsInList,
  ...elementProps
}: UseCommentsEmptyStateProps): {
  EmptyState: JSX.Element | null
  showEmptyStateWithComments: boolean
} => {
  const { unrepliedCount, totalCommentCount } = useUnrepliedCount(
    elementProps.channelIds,
  )

  const { EmptyState } = useEmptyStateElement({
    ...elementProps,
    unrepliedCount,
    totalCommentCount,
  })

  const showEmptyStateWithComments =
    hasCommentsInList &&
    (elementProps.allCommentsLocallyResolved ||
      (elementProps.status === COMMENT_STATUS.All && unrepliedCount === 0))

  return { EmptyState, showEmptyStateWithComments }
}
